import { Component, DestroyRef, effect, inject, Input, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
    MatTableDataSource,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
} from '@angular/material/table';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { filter, forkJoin, lastValueFrom, Observable, of, switchMap } from 'rxjs';
import { RingStates } from 'src/app/models/centrex/enums/ring-states';
import { RuleTargetTypes } from 'src/app/models/centrex/enums/rule-target-types';
import { Rule } from 'src/app/models/centrex/rule';
import { RuleService } from 'src/app/services/centrex/rule.service';
import { DialogDeleteConfirmationComponent } from '../../dialogs/dialog-delete-confirmation/dialog-delete-confirmation.component';
import { ExtensionDisplayPipe } from 'src/app/pipes/extension-display.pipe';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSuffix, MatFormField } from '@angular/material/form-field';
import { MatCheckbox } from '@angular/material/checkbox';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Extension } from 'src/app/models/centrex/extension';

@Component({
    selector: 'app-global-rules',
    templateUrl: './global-rules.component.html',
    styleUrls: ['./global-rules.component.scss'],
    imports: [
        FaIconComponent,
        MatButton,
        RouterLink,
        MatIcon,
        NgClass,
        ReactiveFormsModule,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatSortHeader,
        MatCheckbox,
        MatIconButton,
        MatSuffix,
        MatTooltip,
        MatCellDef,
        MatCell,
        MatFormField,
        MatSelect,
        MatOption,
        MatInput,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        TranslateModule,
    ]
})
export class GlobalRulesComponent {
    private readonly instanceService = inject(InstanceService);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    displayedColumns: string[] = [];
    dataSource: MatTableDataSource<Rule>;
    deleteConfirmationDialog: MatDialogRef<DialogDeleteConfirmationComponent>;

    targetTypes = Object.keys(RuleTargetTypes);
    ringStates = Object.keys(RingStates);

    filtersForm: UntypedFormGroup;

    checkBoxStatus: boolean = null;

    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: false }) sort: MatSort;

    @Input() isReadOnly: boolean = false;

    private _extensionsList: Extension[] = null;

    @Input() set extensionsList(value: Extension[]) {
        if (value) {
            this._extensionsList = value;
        }
    }

    get extensionsList(): Extension[] {
        return this._extensionsList;
    }

    constructor(
        private ruleService: RuleService,
        private extensionService: ExtensionService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService,
        private extensionDisplayPipe: ExtensionDisplayPipe
    ) {
        this.filtersForm = new UntypedFormGroup({});
        this.initFiltersForm();

        effect(() => {
            if (this.instanceService.instance()) {
                this.loadDatas();
                this.initFiltersForm();
            }
        });
    }

    /**
     * Initialisation du formulaire des filtres du tableau des règles
     */
    initFiltersForm() {
        this.filtersForm = this.formbuilder.group({
            id: [''],
            ringState: [''],
            targetType: [''],
            enabled: [this.checkBoxStatus],
            ringSeconds: [''],
            target: [''],
        });
    }

    loadDatas() {
        this.spinner.show();

        let datas: Observable<[Rule[], Extension[]]>;

        if (!this.extensionsList) {
            datas = forkJoin([this.ruleService.getGlobalRules(), this.extensionService.getExtensions()]);
        } else {
            datas = forkJoin([this.ruleService.getGlobalRules(), of(this.extensionsList)]);

            console.log('global-rules: OK');
        }

        datas.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: ([rules, extensions]) => {
                this.extensionsList = extensions;
                rules.forEach((rule) => {
                    rule['targetDisplay'] =
                        rule.targetType === RuleTargetTypes.extension ? this.extensionDisplayPipe.transform(this.extensionsList.find((a) => a.id === rule.target)) : rule.target;
                });

                this.dataSource = new MatTableDataSource(rules);

                this.dataSource.paginator = this.paginator;
                this.dataSource.sort = this.sort;

                this.createFilters();

                this.spinner.hide();
            },
            error: () => {
                this.spinner.hide();
            },
        });
    }

    createFilters() {
        this.dataSource.filterPredicate = (data: Rule, filter: string) => {
            const filterArray = filter.split('¤');
            const matchFilter = [];

            matchFilter.push(data.id && data.id.trim().toLowerCase().includes(filterArray[0].trim().toLowerCase()));

            if (filterArray[1] !== '') {
                matchFilter.push(data.enabled === Boolean(JSON.parse(filterArray[1])));
            }

            const ringStateFilters = filterArray[2] ? filterArray[2].split(',').map((a) => this.translate.instant('EnumRingStates.' + a)) : null;

            if (ringStateFilters) {
                matchFilter.push(data.ringState && ringStateFilters.includes(this.translate.instant('EnumRingStates.' + data.ringState.trim().toLowerCase())));
            }

            const targetTypeFilters = filterArray[3] ? filterArray[3].split(',').map((a) => this.translate.instant('EnumRuleTargetTypes.' + a)) : null;

            if (targetTypeFilters) {
                matchFilter.push(data.targetType && targetTypeFilters.includes(this.translate.instant('EnumRuleTargetTypes.' + data.targetType.trim().toLowerCase())));
            }

            if (filterArray[4] !== '') {
                matchFilter.push(data.ringSeconds && data.ringSeconds.toString().trim().toLowerCase().includes(filterArray[4].trim().toLowerCase()));
            }

            if (filterArray[5] !== '') {
                matchFilter.push(data['targetDisplay'] && data['targetDisplay'].trim().toLowerCase().includes(filterArray[5].trim().toLowerCase()));
            }

            return matchFilter.every(Boolean);
        };

        this.displayedColumns = ['enabled', 'ringState', 'targetType', 'ringSeconds', 'target'];

        if (this.isReadOnly === false) {
            this.displayedColumns.push('actions');
        }

        this.applyFilters();
    }

    /**
     * Application du filtre dans le tableau des règles
     */
    applyFilters() {
        const id = this.filtersForm.get('id').value;
        const enabled = this.filtersForm.get('enabled').value;
        const ringState = this.filtersForm.get('ringState').value;
        const targetType = this.filtersForm.get('targetType').value;
        const ringSeconds = this.filtersForm.get('ringSeconds').value;
        const target = this.filtersForm.get('target').value;

        const filterValue =
            (!!id ? id : '') +
            '¤' +
            (enabled !== null ? enabled : '') +
            '¤' +
            (!!ringState ? ringState : '') +
            '¤' +
            (!!targetType ? targetType : '') +
            '¤' +
            (!!ringSeconds ? ringSeconds : '') +
            '¤' +
            (!!target ? target : '');

        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    /**
     * Réinitialise le filtre de la colonne spécifiée.
     * @param formControlName Nom du contrôle du formulaire
     */
    resetColumnFilter(formControlName: string): void {
        if (formControlName === 'enabled') {
            this.checkBoxStatus = null;
        }

        this.filtersForm.controls[formControlName].reset();
        this.applyFilters();
    }

    /**
     * Réinitialise tous les filtres de colonne
     */
    resetAllColumnFilter() {
        this.checkBoxStatus = null;
        this.filtersForm.reset();
        this.applyFilters();
    }

    onDeleteClick(ruleId: string) {
        this.deleteConfirmationDialog = this.dialog.open(DialogDeleteConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });
        this.deleteConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogDeleteConfirmation.GlobalRuleConfirmMessage', { ruleId: ruleId });

        this.deleteConfirmationDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => this.ruleService.deleteGlobalRule(ruleId)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.toastr.success(this.translate.instant('GlobalRules.SuccessMessageDeleteGlobalRule', { ruleId: ruleId }));
                    this.loadDatas();
                },
                error: () => {
                    this.toastr.error(this.translate.instant('GlobalRules.ErrorMessageDeleteGlobalRule', { ruleId: ruleId }));
                },
                complete: () => {
                    this.deleteConfirmationDialog = null;
                },
            });
    }

    checkBoxChange() {
        switch (this.checkBoxStatus) {
            case false:
                this.checkBoxStatus = null;
                this.filtersForm.get('enabled').setValue(null);
                break;

            case null:
                this.checkBoxStatus = true;
                break;

            case true:
                this.checkBoxStatus = false;
                break;
        }

        this.applyFilters();
    }
}
